import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Autocomplete, { highlightSubstring } from '../Autocomplete';
import Dialog from '../Dialog';
import Form from '../Form';
import FormActions from '../Form/FormActions';
import Icon from '../Icon';
import TextField from '../TextField';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import { operations as operationsApi } from '@afosto/content/api/Operations';
import translations from './translations';

const StorageUploadDialog = ({ file, isOpen, onClose, onError, onSuccess, storages }) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const formattedStorages = storages.reduce(
    (acc, storage) => [
      ...acc,
      {
        label: storage.name ?? storage.id?.toString() ?? '',
        value: storage.id?.toString(),
        type: storage.type ?? '',
      },
    ],
    [],
  );

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleOnSubmit = async data => {
    setIsLoading(true);

    try {
      const response = await operationsApi.cloneObject(data?.storage?.value, {
        file_id: file?.id,
        path: data?.path,
      });

      const taskID = response?.headers?.['x-task-id'];

      await scheduleTask(taskID, {
        onSuccess: () => {
          onSuccess();
          setIsLoading(false);
        },
        onError: () => {
          onError();
        },
      });
    } catch (err) {
      // do something
    } finally {
      setIsLoading(false);
    }
  };

  const form = useForm({
    defaultValues: {
      storage: null,
      path: '',
    },
    onSubmit: handleOnSubmit,
    onCancel: () => {
      form.clear();
      onClose();
    },
  });
  const { setValue } = form;

  const handleChangeStorage = (ev, newValue) => {
    setValue('storage', newValue);
    setError(null);
  };

  const renderOption = (option, { inputValue = '' } = {}) => {
    let optionLabel = option;

    if (typeof option === 'object') {
      // eslint-disable-next-line prefer-destructuring
      optionLabel = option.label;
    }

    let icon = ['fal', 'server'];
    if (option?.type === 'dropbox') {
      icon = ['fab', 'dropbox'];
    }

    return (
      <ListItem component="div" dense disableGutters>
        <ListItemIcon>
          <Icon icon={icon} />
        </ListItemIcon>
        <ListItemText
          primary={highlightSubstring(optionLabel, inputValue)}
          secondary={option.type}
        />
      </ListItem>
    );
  };

  return (
    <Dialog open={isOpen} maxWidth="xs" fullWidth onClose={onClose}>
      <LoadingWrapper isLoading={isLoading}>
        <DialogTitle>
          <FormattedMessage {...translations.storageUploadDialog.title} />
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 24 }}>
          <Form form={form} renderActions={false}>
            {error?.message && (
              <Box pb={2}>
                <Typography variant="body2" color="error">
                  {error.message}
                </Typography>
              </Box>
            )}
            <Autocomplete
              name="storage"
              options={formattedStorages}
              onChange={handleChangeStorage}
              label={intl.formatMessage(translations.storageUploadDialog.fields.externalStorages)}
              renderOption={renderOption}
              autocomplete="off"
            />
            <TextField
              name="path"
              label={intl.formatMessage(translations.storageUploadDialog.fields.path)}
            />
          </Form>
          <FormActions
            primaryAction={{
              label: intl.formatMessage(translations.storageUploadDialog.actions.upload),
              onClick: form.onSubmit,
              type: 'submit',
              color: 'primary',
              variant: 'contained',
            }}
            secondaryAction={{
              label: intl.formatMessage(translations.storageUploadDialog.actions.cancel),
              onClick: form.onCancel,
            }}
          />
        </DialogContent>
      </LoadingWrapper>
    </Dialog>
  );
};

StorageUploadDialog.propTypes = {
  file: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  storages: PropTypes.array,
};

StorageUploadDialog.defaultProps = {
  file: {},
  isOpen: false,
  onClose: undefined,
  onSuccess: undefined,
  onError: undefined,
  storages: [],
};

export default StorageUploadDialog;
